"use client";

import Link from "next/link";
import { ArrowRight, Cpu, ShieldCheck, Megaphone, Telescope } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { whatWeDo as data } from "@/lib/data/about";

const iconMap = { Cpu, ShieldCheck, Megaphone, Telescope } as const;
const accentMap = {
  crimson: "var(--color-crimson)",
  purple: "var(--color-purple)",
  cyan: "var(--color-cyan)",
  emerald: "var(--color-emerald)",
  amber: "var(--color-amber)",
  denim: "var(--color-denim)",
} as const;

export function WhatWeDo() {
  return (
    <Section id="what-we-do" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/3 left-1/3 h-72 w-72 rounded-full bg-[var(--color-cyan)] opacity-15 blur-[120px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {data.pillars.map((pillar, i) => {
            const Icon = iconMap[pillar.icon as keyof typeof iconMap] ?? Cpu;
            const accent = accentMap[pillar.accent];
            return (
              <Reveal key={pillar.title} delay={i * 80}>
                <div
                  className="glass rounded-2xl p-6 h-full relative overflow-hidden group hover:scale-[1.01] transition-transform"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)`,
                  }}
                >
                  <div
                    className="pointer-events-none absolute -top-20 -right-20 h-48 w-48 rounded-full opacity-20 blur-3xl"
                    style={{ background: accent }}
                  />
                  <div className="relative">
                    <div className="flex items-center justify-between">
                      <span
                        className="grid place-items-center h-11 w-11 rounded-xl"
                        style={{
                          background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                        }}
                      >
                        <Icon className="h-5 w-5" style={{ color: accent }} />
                      </span>
                      <span
                        className="eyebrow-tiny font-bold"
                        style={{ color: accent }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="mt-5">{pillar.title}</h3>
                    <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{pillar.body}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={400}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link
                key={c.label}
                href={c.href}
                className={
                  i === 0
                    ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                    : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                }
              >
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
